/**
 * Connect Stedi Step
 *
 * First step in billing setup wizard - connects organization to Stedi.
 * User enters their Stedi API key to establish the connection.
 *
 * Uses provider billing permissions or the selected organization's admin twin.
 * Staff observers can read connection status without connection controls.
 */

import { useEffect, useRef, useState } from 'react';
import {
  useStediConnectionAdmin,
  useStediConnectionProvider,
  useStediStatusAdmin,
  useStediStatusProvider,
} from '@features/stedi-connection/store/hooks';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';

// Signing up through this link installs the JotPsych connection on its own.
const STEDI_SIGN_UP_URL =
  'https://portal.stedi.com/auth/sign-up?originatingApp=jotpsych&redirect_uri=https%3A%2F%2Fportal.stedi.com%2Fapp%2Fcreate-account%3ForiginatingApp%3Djotpsych';
const STEDI_SETUP_GUIDE_URL =
  'https://jotlify.jotpsych.com/share/stedi-account-setup-9295b0d582f5b5927f11665eb1f7c921';

interface ConnectStediStepProps {
  onNext?: () => void;
  onConnected: () => void;
  /** Presence selects the admin twin. Never impersonate the selected provider. */
  organizationId?: string;
  readOnly?: boolean;
  onAction?: (action: 'connect' | 'reconnect' | 'cancel') => void;
}

export function ConnectStediStep(props: ConnectStediStepProps) {
  // Remount both local secret state and mutations whenever the org scope changes.
  return (
    <ScopedConnectStediStep
      key={`${props.organizationId ?? 'provider'}:${Boolean(props.readOnly)}`}
      {...props}
    />
  );
}

function ScopedConnectStediStep({
  onNext,
  onConnected,
  organizationId,
  onAction,
  readOnly = false,
}: ConnectStediStepProps) {
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const mounted = useRef(true);

  const admin = organizationId !== undefined;
  const providerStatus = useStediStatusProvider({ skip: admin });
  const adminStatus = useStediStatusAdmin(organizationId ?? null, { skip: !admin });
  const {
    status,
    isConnected,
    isLoading,
    isFetching,
    isError,
    refetch: refetchStatus,
  } = admin ? adminStatus : providerStatus;
  const providerConnection = useStediConnectionProvider();
  const adminConnection = useStediConnectionAdmin();
  const { connectLoading, resetConnect } = admin ? adminConnection : providerConnection;
  const resetConnectRef = useRef(resetConnect);
  resetConnectRef.current = resetConnect;
  const pendingConnect = useRef<
    | ReturnType<typeof providerConnection.connect>
    | ReturnType<typeof adminConnection.connect>
    | undefined
  >(undefined);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingConnect.current?.reset();
      pendingConnect.current = undefined;
    };
  }, []);

  // If already connected, show success message
  const alreadyConnected = isConnected && status?.connected && !reconnecting;

  const handleConnect = async () => {
    if (readOnly) return;
    if (!apiKey.trim()) {
      setError('Please enter your Stedi API key');
      return;
    }

    setError(null);
    onAction?.('connect');

    const connectStediRequest = { api_key: apiKey.trim() };
    const request = admin
      ? adminConnection.connect({ organizationId, connectStediRequest })
      : providerConnection.connect({ connectStediRequest });
    pendingConnect.current = request;
    try {
      await request.unwrap();
    } catch {
      if (mounted.current) {
        // Do not render a server-supplied message that could echo the submitted credential.
        setError(
          "The API key doesn't appear to be valid. Please check that you copied the full key from Stedi.",
        );
      }
      return;
    } finally {
      request.reset();
      if (pendingConnect.current === request) {
        pendingConnect.current = undefined;
        // Clear useMutation's own promise/originalArgs as well as the store result.
        if (mounted.current) resetConnectRef.current();
      }
    }
    if (!mounted.current) return;
    setApiKey('');
    setReconnecting(false);
    const refreshed = await refetchStatus();
    if (mounted.current && refreshed.data?.connected) onConnected();
  };

  if (isLoading || (!status && isFetching))
    return <CircularProgress aria-label="Checking Stedi connection" size={24} />;
  if (isError)
    return (
      <Alert severity="error" action={<Button onClick={() => void refetchStatus()}>Retry</Button>}>
        We couldn&apos;t check your Stedi connection. Please try again.
      </Alert>
    );

  if (readOnly && !alreadyConnected) {
    return (
      <Alert severity="info">
        Stedi is not connected. A JotPsych administrator can connect this clinic’s account.
      </Alert>
    );
  }

  if (alreadyConnected) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48, mr: 2 }} />
          <Box>
            <Typography variant="h6">Stedi connected</Typography>
            <Typography variant="body2" color="text.secondary">
              Your practice is connected to the clearinghouse
            </Typography>
          </Box>
        </Box>

        {status?.account_label && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Connected to: <strong>{status.account_label}</strong>
            {status.mode && ` (${status.mode})`}
          </Alert>
        )}

        {!readOnly && (
          <Button
            onClick={() => {
              setReconnecting(true);
              setError(null);
              onAction?.('reconnect');
            }}
          >
            Reconnect
          </Button>
        )}
        {!readOnly && onNext && (
          <Button variant="contained" onClick={onNext} fullWidth>
            Continue to Next Step
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Connect Your Clearinghouse
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
        JotPsych partners with Stedi, a healthcare clearinghouse, to route your claims to insurance
        companies. Enter your Stedi API key below to connect.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
        Don&apos;t have a Stedi account?{' '}
        <Link href={STEDI_SIGN_UP_URL} target="_blank" rel="noopener noreferrer">
          Create a free account
        </Link>{' '}
        and install the JotPsych app, then{' '}
        <Link href={STEDI_SETUP_GUIDE_URL} target="_blank" rel="noopener noreferrer">
          follow these setup instructions
        </Link>{' '}
        to get your API key.
      </Typography>

      <Box
        component="form"
        onSubmit={event => {
          event.preventDefault();
          void handleConnect();
        }}
        sx={{ mt: 3 }}
      >
        <TextField
          label="Stedi API Key"
          type="password"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          fullWidth
          required
          error={!!error}
          helperText={error || 'Your API key will be stored securely'}
          disabled={connectLoading}
          autoComplete="off"
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            type="submit"
            disabled={connectLoading}
            fullWidth
            startIcon={connectLoading ? <CircularProgress size={20} /> : undefined}
          >
            {connectLoading ? 'Connecting...' : 'Connect'}
          </Button>
          {reconnecting && (
            <Button
              disabled={connectLoading}
              onClick={() => {
                setApiKey('');
                setError(null);
                setReconnecting(false);
                onAction?.('cancel');
              }}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
